import {
  Car,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  Shield,
  Star,
  Users,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useState } from "react";
import React from "react";

import appGalleryIcon from "@/assets/apps/appgallery.png";
import appStoreIcon from "@/assets/apps/appstore.png";
import playStoreIcon from "@/assets/apps/playstore.jpg";
import webIcon from "@/assets/apps/web.jfif";
import appPreview from "@/assets/images/onboarding1.png";
import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { fetchAppDownloadSection } from "@/services/landing/hareengEatsService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";

export default function Rides() {
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);

  const [androidUrl, setAndroidUrl] = useState<string>("");
  const [iosUrl, setIosUrl] = useState<string>("");
  const [webUrl, setWebUrl] = useState<string>("");
  const [appGalleryUrl, setAppGalleryUrl] = useState<string>("");

  useEffect(() => {
    Promise.all([
      fetchAppDownloadSection(),
      fetchHeroBackgrounds("catchrides"),
    ]).then(([appSection, heroData]) => {
      if (appSection) {
        setAndroidUrl(appSection.androidUrl || "");
        setIosUrl(appSection.iosUrl || "");
        setWebUrl(appSection.webUrl || "");
        setAppGalleryUrl(appSection.appGalleryUrl || "");
      }
      if (heroData) {
        setHeroBackground(heroData);
      }
    });
  }, []);
  
  const [showQR, setShowQR] = useState(false);
  const [qrPlatform, setQrPlatform] = useState<"android" | "appgallery" | "ios" | "web">("android");
  const [rideTypes, setRideTypes] = useState<any[]>([]);
  const [rideTypesLoading, setRideTypesLoading] = useState(true);

  useEffect(() => {
    async function loadRideOptions() {
      try {
        const data = await import("@/services/landing/catchRidesService");
        const rideOptions = await data.fetchRideOptions();
        // Map backend data to UI format
        setRideTypes(
          rideOptions.map((ride: any) => ({
            description: ride.description,
            features: Array.isArray(ride.features)
              ? ride.features
              : typeof ride.features === "string"
                ? JSON.parse(ride.features)
                : [],
            icon: ride.title?.toLowerCase().includes("comfort") ? (
              <Star className="text-secondary" size={32} />
            ) : ride.title?.toLowerCase().includes("xl") ? (
              <Users className="text-accent" size={32} />
            ) : (
              <Car className="text-primary" size={32} />
            ),
            image_path: ride.image_path,
            name: ride.title,
            price: ride.price ? `From R${ride.price}` : "",
          })),
        );
      } catch (e) {
        setRideTypes([]);
      }
      setRideTypesLoading(false);
    }
    loadRideOptions();
  }, []);

  const features = [
    {
      description: "Average pickup time under 5 minutes",
      icon: <Clock className="text-primary" size={32} />,
      title: "Quick Pickup",
    },
    {
      description: "All drivers background-checked and insured",
      icon: <Shield className="text-success" size={32} />,
      title: "Safe & Secure",
    },
    {
      description: "No surprises - see your fare before you book",
      icon: <DollarSign className="text-warning" size={32} />,
      title: "Upfront Pricing",
    },
    {
      description: "Quality drivers with 4.8+ star ratings",
      icon: <Star className="text-info" size={32} />,
      title: "Rated Drivers",
    },
  ];

  // Slider state
  const [slideIndex, setSlideIndex] = useState(0);

  // Responsive items per view
  const getItemsPerView = () => {
    if (window.innerWidth < 640) return 1; // mobile
    if (window.innerWidth < 1024) return 2; // tablet
    return 3; // desktop
  };

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

  // Update itemsPerView on resize
  React.useEffect(() => {
    const handleResize = () => setItemsPerView(getItemsPerView());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const maxIndex = Math.max(0, rideTypes.length - itemsPerView);

  const handlePrev = () => setSlideIndex((i) => Math.max(i - 1, 0));
  const handleNext = () => setSlideIndex((i) => Math.min(i + 1, maxIndex));

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="relative -mt-16 overflow-hidden pt-16">
          <div className="absolute inset-0 opacity-20">
            <img
              alt={heroBackground?.imageAlt || "City rides"}
              className="h-full w-full object-cover"
              src={
                heroBackground?.imageUrl ||
                "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1200&h=600&fit=crop&crop=center"
              }
            />
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                  Get there with <span className="text-accent">Hareeng</span>
                </h1>
                <p className="mb-8 text-xl opacity-90">
                  Safe, reliable rides at your fingertips. Choose from multiple
                  ride options to fit your needs and budget.
                </p>

                <div className="mb-8 flex flex-col justify-center gap-4 sm:flex-row">
                  <button
                    className="btn btn-primary btn-lg font-semibold"
                    disabled={!androidUrl && !iosUrl && !appGalleryUrl}
                    onClick={() => {
                      setQrPlatform(androidUrl ? "android" : iosUrl ? "ios" : "appgallery");
                      setShowQR(true);
                    }}
                  >
                    Download Our Apps
                  </button>
                  {!webUrl ? (
                    <button className="btn btn-outline btn-lg font-semibold" disabled>
                      Request Online
                    </button>
                  ) : (
                    <a 
                      className="btn btn-outline btn-lg font-semibold" 
                      href={webUrl} 
                      rel="noopener noreferrer" 
                      target="_blank"
                    >
                      Request Online
                    </a>
                  )}
                </div>

                {showQR && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-4">
                    <div className="relative flex w-full max-w-md flex-col rounded-3xl bg-white shadow-2xl dark:bg-base-800 sm:w-96">
                      {/* Header */}
                      <div className="flex items-center justify-between border-b border-base-200 px-6 py-4">
                        <div>
                          <h3 className="text-xl font-bold text-base-content">Download Our Apps</h3>
                          <p className="text-sm text-base-content/60">Choose your platform</p>
                        </div>
                        <button
                          aria-label="Close"
                          className="flex h-10 w-10 items-center justify-center rounded-full text-xl font-bold text-base-content/50 transition-colors hover:bg-base-200 hover:text-primary"
                          onClick={() => setShowQR(false)}
                        >
                          ×
                        </button>
                      </div>

                      {/* Tabs */}
                      <div className="flex gap-1 border-b border-base-200 px-6 py-3 overflow-x-auto">
                        {androidUrl && (
                          <button
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                              qrPlatform === "android"
                                ? "border-b-2 border-primary bg-primary/10 text-primary"
                                : "text-base-content/60 hover:text-base-content"
                            }`}
                            onClick={() => setQrPlatform("android")}
                          >
                            <img alt="Play Store" className="h-5 w-5" src={playStoreIcon} />
                            <span>Android</span>
                          </button>
                        )}
                        {iosUrl && (
                          <button
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                              qrPlatform === "ios"
                                ? "border-b-2 border-primary bg-primary/10 text-primary"
                                : "text-base-content/60 hover:text-base-content"
                            }`}
                            onClick={() => setQrPlatform("ios")}
                          >
                            <img alt="App Store" className="h-5 w-5" src={appStoreIcon} />
                            <span>iOS</span>
                          </button>
                        )}
                        {webUrl && (
                          <button
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                              qrPlatform === "web"
                                ? "border-b-2 border-primary bg-primary/10 text-primary"
                                : "text-base-content/60 hover:text-base-content"
                            }`}
                            onClick={() => setQrPlatform("web")}
                          >
                            <img alt="Web" className="h-5 w-5" src={webIcon} />
                            <span>Web</span>
                          </button>
                        )}
                        {appGalleryUrl && (
                          <button
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all whitespace-nowrap ${
                              qrPlatform === "appgallery"
                                ? "border-b-2 border-primary bg-primary/10 text-primary"
                                : "text-base-content/60 hover:text-base-content"
                            }`}
                            onClick={() => setQrPlatform("appgallery")}
                          >
                            <img alt="App Gallery" className="h-5 w-5" src={appGalleryIcon} />
                            <span>AppGallery</span>
                          </button>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex flex-col items-center px-6 py-8">
                        {/* QR Code Container */}
                        <div className="mb-6 flex items-center justify-center bg-white p-4 rounded-lg">
                          <QRCodeCanvas
                            bgColor="#ffffff"
                            fgColor="#000000"
                            includeMargin={true}
                            level="M"
                            size={240}
                            value={
                              qrPlatform === "android"
                                ? androidUrl
                                : qrPlatform === "ios"
                                  ? iosUrl
                                  : qrPlatform === "appgallery"
                                    ? appGalleryUrl
                                    : webUrl
                            }
                          />
                        </div>

                        {/* Description */}
                        <p className="mb-4 text-center text-sm text-base-content/70">
                          Scan with your phone camera
                        </p>

                        {/* Direct Link Button */}
                        <a
                          className="btn btn-primary w-full"
                          href={
                            qrPlatform === "android"
                              ? androidUrl
                              : qrPlatform === "ios"
                                ? iosUrl
                                : qrPlatform === "appgallery"
                                  ? appGalleryUrl
                                  : webUrl
                          }
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          Open {qrPlatform === "android" ? "Android" : qrPlatform === "ios" ? "iOS" : qrPlatform === "appgallery" ? "AppGallery" : "Web"} Store
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-8 grid grid-cols-3 gap-4 text-center">
                  <center>
                    <div>
                      <div className="text-2xl font-bold">24/7</div>
                      <div className="text-sm opacity-80">Available</div>
                    </div>
                  </center>
                </div>
              </div>

              <div className="relative">
                <div className="flex flex-col items-center rounded-2xl bg-base-100 p-6 shadow-2xl">
                  <img
                    alt="Hareeng App Preview"
                    className="mb-6 h-48 w-48 object-contain"
                    src={appPreview}
                  />
                  <h3 className="mb-2 text-xl font-bold text-base-content">
                    Get the Hareeng App
                  </h3>
                  <p className="mb-4 text-center text-base-content/70">
                    Book rides, track drivers, and more. Explore now for the
                    best experience!
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 w-full">
                    {androidUrl && (
                      <a
                        className="btn btn-sm btn-ghost"
                        href={androidUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                        title="Open Android app"
                      >
                        <img alt="Play Store" className="h-4 w-4" src={playStoreIcon} />
                        <span className="hidden sm:inline">Android</span>
                      </a>
                    )}
                    {iosUrl && (
                      <a
                        className="btn btn-sm btn-ghost"
                        href={iosUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                        title="Open iOS app"
                      >
                        <img alt="App Store" className="h-4 w-4" src={appStoreIcon} />
                        <span className="hidden sm:inline">iOS</span>
                      </a>
                    )}
                    {appGalleryUrl && (
                      <a
                        className="btn btn-sm btn-ghost"
                        href={appGalleryUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                        title="Open AppGallery app"
                      >
                        <img alt="App Gallery" className="h-4 w-4" src={appGalleryIcon} />
                        <span className="hidden sm:inline">AppGallery</span>
                      </a>
                    )}
                    {webUrl && (
                      <a
                        className="btn btn-sm btn-ghost"
                        href={webUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                        title="Open Web app"
                      >
                        <img alt="Web" className="h-4 w-4" src={webIcon} />
                        <span className="hidden sm:inline">Web</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ride Types */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Choose Your Ride</h2>
              <p className="text-xl text-base-content/70">
                Different options for different needs
              </p>
            </div>

            <div className="relative">
              <div
                className="flex gap-8 transition-all duration-300"
                style={{
                  minHeight: 400,
                }}
              >
                {rideTypes
                  .slice(slideIndex, slideIndex + itemsPerView)
                  .map((ride) => (
                    <div
                      className="card min-w-0 flex-1 bg-base-100 shadow-xl transition-shadow hover:shadow-2xl"
                      key={ride.name}
                      style={{
                        maxWidth:
                          itemsPerView === 1
                            ? "100%"
                            : itemsPerView === 2
                              ? "50%"
                              : "33.3333%",
                      }}
                    >
                      <figure className="relative h-48">
                        {(() => {
                          const url = ride.image_path
                            ? `${import.meta.env.VITE_API_STORAGE_URL}/storage/${ride.image_path}`
                            : appPreview;

                          return (
                            <img
                              alt={ride.name}
                              className="h-full w-full object-cover"
                              src={url}
                            />
                          );
                        })()}
                        <div className="absolute left-4 top-4 rounded-full bg-base-100 p-2">
                          {ride.icon}
                        </div>
                      </figure>

                      <div className="card-body">
                        <div className="flex items-center justify-between">
                          <h3 className="card-title text-xl">{ride.name}</h3>
                          <span className="text-lg font-bold text-primary">
                            {ride.price}
                          </span>
                        </div>

                        <p className="text-base-content/70">
                          {ride.description}
                        </p>

                        <div className="mt-4 space-y-2">
                          {ride.features.map((feature: string, idx: number) => (
                            <div className="flex items-center gap-2" key={idx}>
                              <CheckCircle className="text-success" size={16} />
                              <span className="text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                        {/* Removed select button */}
                      </div>
                    </div>
                  ))}
              </div>
              <div className="mt-8 flex justify-center gap-4">
                <button
                  aria-label="Previous"
                  className="btn btn-outline flex items-center gap-2"
                  disabled={slideIndex === 0}
                  onClick={handlePrev}
                >
                  <ChevronLeft size={20} />
                  <span className="hidden sm:inline">Back</span>
                </button>
                <button
                  aria-label="Next"
                  className="btn btn-outline flex items-center gap-2"
                  disabled={slideIndex === maxIndex}
                  onClick={handleNext}
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Why Choose Hareeng?</h2>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => (
                <div className="text-center" key={index}>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-200">
                    {feature.icon}
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">
                    {feature.title}
                  </h3>
                  <p className="text-base-content/70">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* App Promotion */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">
                Download the Hareeng App
              </h2>
              <p className="text-xl text-base-content/70">
                Book rides, track drivers, and manage your trips seamlessly.
              </p>
            </div>

            <div className="relative">
              <div className="flex flex-col items-center rounded-2xl bg-base-100 p-6 shadow-2xl">
                <img
                  alt="Hareeng App Preview"
                  className="mb-6 h-48 w-48 object-contain"
                  src={appPreview}
                />
                <h3 className="mb-2 text-xl font-bold text-base-content">
                  Get the Hareeng App
                </h3>
                <p className="mb-4 text-center text-base-content/70">
                  Book rides, track drivers, and more. Download now for the best
                  experience!
                </p>
                <div className="flex flex-col justify-center gap-3 sm:flex-row">
                  <button
                    className="btn btn-primary btn-lg font-semibold"
                    disabled={!androidUrl && !iosUrl && !appGalleryUrl && !webUrl}
                    onClick={() => {
                      setQrPlatform(androidUrl ? "android" : iosUrl ? "ios" : appGalleryUrl ? "appgallery" : "web");
                      setShowQR(true);
                    }}
                  >
                    Download Our Apps
                  </button>
                  {!webUrl ? (
                    <button className="btn btn-outline btn-lg font-semibold" disabled>
                      Request Online
                    </button>
                  ) : (
                    <a 
                      className="btn btn-outline btn-lg font-semibold" 
                      href={webUrl} 
                      rel="noopener noreferrer" 
                      target="_blank"
                    >
                      Request Online
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
